import { forwardRef, type InputHTMLAttributes } from 'react';

import { cn } from '@/lib/utils';

export type SwitchSize = 'sm' | 'md' | 'lg';

export type SwitchProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'> & {
  label?: string;
  description?: string;
  size?: SwitchSize;
};

const sizeStyles = {
  sm: { track: 'w-8 h-5', thumb: 'w-3.5 h-3.5', translate: 'peer-checked:translate-x-3.5' },
  md: { track: 'w-11 h-6', thumb: 'w-4.5 h-4.5', translate: 'peer-checked:translate-x-5' },
  lg: { track: 'w-14 h-8', thumb: 'w-6 h-6', translate: 'peer-checked:translate-x-6' },
};

export const Switch = forwardRef<HTMLInputElement, SwitchProps>(
  ({ label, description, size = 'md', className = '', id, disabled, ...props }, ref) => {
    const inputId = id || label?.toLowerCase().replace(/\s+/g, '-');
    const styles = sizeStyles[size];

    return (
      <label
        htmlFor={inputId}
        className={cn(
          'group inline-flex items-start gap-3 cursor-pointer',
          disabled && 'opacity-50 cursor-not-allowed',
          className,
        )}
      >
        <div className="relative flex items-center">
          <input
            ref={ref}
            id={inputId}
            type="checkbox"
            role="switch"
            disabled={disabled}
            className="peer sr-only"
            {...props}
          />
          <div
            className={cn(
              styles.track,
              'rounded-full transition-colors duration-200',
              'bg-surface-700 peer-checked:bg-brand-500',
              'peer-focus-visible:ring-2 peer-focus-visible:ring-brand-500 peer-focus-visible:ring-offset-2 peer-focus-visible:ring-offset-surface-900',
              !disabled && 'group-hover:bg-surface-600 peer-checked:group-hover:bg-brand-400',
            )}
          />
          <div
            className={cn(
              'absolute left-0.5 rounded-full bg-white shadow-sm transition-transform duration-200 ease-in-out',
              styles.thumb,
              styles.translate,
            )}
          />
        </div>
        {(label || description) && (
          <div className="flex flex-col">
            {label && <span className="text-sm font-medium text-white">{label}</span>}
            {description && <span className="text-sm text-gray-400">{description}</span>}
          </div>
        )}
      </label>
    );
  },
);

Switch.displayName = 'Switch';
